// src/hoc/withTranslation.jsx - Optimized
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { t, addLanguageChangeListener, removeLanguageChangeListener } from '../i18n';

/**
 * Geliştirilmiş Çeviri Higher Order Component (HOC)
 * Bileşenlere çeviri özelliklerini ekler, performans optimizasyonu sağlar
 * 
 * @param {React.Component} WrappedComponent - Sarmalanan bileşen
 * @returns {React.Component} Çeviri özellikleri eklenmiş bileşen
 */
const withTranslation = (WrappedComponent) => {
  // Daha açıklayıcı ve dikkat çekici bir isim kullan
  const WithTranslationEnhanced = (props) => {
    const { currentLanguage } = useLanguage();
    // useRef ile değişmez bir referans sağlayabilirdik ancak dil değişiminde
    // yeniden render için useState kullanmak daha uygun
    const [renderKey, setRenderKey] = useState(0);
    
    // Dilden bağımsız props değişimi için ayrı bir key
    const [propsKey, setPropsKey] = useState(0);
    
    // Dile özel performans iyileştirmesi
    const forceLanguageUpdate = useCallback(() => {
      // console.log("Dil değişikliği algılandı, bileşen yeniden render ediliyor");
      setRenderKey(prev => prev + 1);
    }, []);
    
    // Dilden bağımsız prop değişimlerini tespit et (Advanced)
    useEffect(() => {
      setPropsKey(prev => prev + 1);
    }, [props]);
    
    // Dil değişikliklerini dinleyen optimize edilmiş bir etki
    useEffect(() => {
      // Dil değişikliği dinleyicisi ekle
      addLanguageChangeListener(forceLanguageUpdate);
      
      // Global dil değişim olayını da dinle (tarayıcı olayı)
      const handleGlobalLanguageChange = (event) => {
        // console.log(`Global dil değişim olayı algılandı: ${event.detail.language}`);
        forceLanguageUpdate();
      };
      
      // Tarayıcı olayına abone ol (global senkronizasyon)
      window.addEventListener('app:language:changed', handleGlobalLanguageChange);
      
      // Temizleme işlevi - abonelikleri düzgünce kaldır
      return () => {
        removeLanguageChangeListener(forceLanguageUpdate);
        window.removeEventListener('app:language:changed', handleGlobalLanguageChange);
      };
    }, [forceLanguageUpdate]);
    
    // t fonksiyonunu useMemo ile optimize et
    const wrappedT = useCallback((key, params) => {
      return t(key, params);
    }, [currentLanguage]); // Sadece dil değiştiğinde yeniden oluştur
    
    // İnce ayar: Component Props + Translation Props ile memoize
    const combinedProps = useMemo(() => ({
      ...props,
      t: wrappedT,
      currentLanguage,
      renderKey,
      propsKey
    }), [props, wrappedT, currentLanguage, renderKey, propsKey]);
    
    // Sarmalanan bileşeni, dil ve çeviri özellikleriyle render et
    return <WrappedComponent {...combinedProps} />;
  };
  
  // Anlamlı bir DisplayName ekle (debug için)
  const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';
  WithTranslationEnhanced.displayName = `withTranslation(${displayName})`;
  
  return WithTranslationEnhanced;
};

export default withTranslation;